import { process, State } from '@progress/kendo-data-query';
import {
  SUPPLIER_EXPORT_COMPANY_CHUNK_SIZE,
  SUPPLIER_EXPORT_MAX_CONCURRENT,
} from 'src/constants/manageSuppliers';
import { buildSupplierExportFilterRequest } from 'src/services/manage_suppliers/buildSupplierFilterRequest';
import { mapSupplier } from 'src/services/manage_suppliers/supplierMapper';
import { fetchSuppliers } from 'src/services/manage_suppliers/useSuppliersQuery';
import {
  ManageSupplier,
  ManageSupplierTypeFilterItem,
} from 'src/types/manage-suppliers/ManageSupplier';
import { SupplierFilterItem } from 'src/types/manage-suppliers/SupplierFilter';

export interface FetchSuppliersForExportParams {
  statusFilter: number;
  supplierTypeFilter: ManageSupplierTypeFilterItem[];
  /** Raw value from useAuth() — parsed to a number here. */
  companyId: string | number | undefined;
  /** Companies available to the user (useAuth().companies ids); decides whether to chunk. */
  companyIds?: ReadonlyArray<string | number>;
  filterBy?: string;
  filter: State['filter'];
  sort: State['sort'];
}

const toChunks = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

const runWithConcurrency = async <T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> => {
  const results = new Array<T>(tasks.length);
  let index = 0;
  const runNext = async (): Promise<void> => {
    while (index < tasks.length) {
      const i = index++;
      results[i] = await tasks[i]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, () => runNext()));
  return results;
};

// Each chunk comes back sorted by supplier name (case-insensitive, nulls last), but the
// concatenation is not, so restore name ordering before the grid sort is applied.
// This approximates Oracle BINARY_CI; equal names have no backend tie-breaker.
const bySupplierCompanyName = (a: SupplierFilterItem, b: SupplierFilterItem): number => {
  const left = a.supplierCompanyName?.toLowerCase();
  const right = b.supplierCompanyName?.toLowerCase();
  if (left == null || right == null) return left == null ? (right == null ? 0 : 1) : -1;
  if (left === right) return 0;
  return left < right ? -1 : 1;
};

// Up to SUPPLIER_EXPORT_COMPANY_CHUNK_SIZE companies: one request covering every company under
// companyId, exactly as before. More than that: one request per chunk of company ids, run
// concurrently. The backend returns each export row from exactly one chunk (rows that are not
// tied to a listed company come back only with the chunk containing companyId), so the chunks
// are concatenated as-is. Do not dedupe: the same supplier/catalog legitimately appears once per
// property, and the single-request export can itself contain repeated rows.
const fetchExportItems = async (
  params: FetchSuppliersForExportParams,
  companyId: number
): Promise<SupplierFilterItem[]> => {
  const baseParams = {
    statusFilter: params.statusFilter,
    supplierTypeFilter: params.supplierTypeFilter,
    companyId,
  };
  const companyIds = params.companyIds ?? [];
  // The legacy path does not use this collection. For chunked exports, reject malformed
  // IDs rather than silently omitting a company's rows from an otherwise successful file.
  const uniqueCompanyIds = [...new Set(companyIds.map(Number))];
  if (
    companyIds.length > SUPPLIER_EXPORT_COMPANY_CHUNK_SIZE &&
    uniqueCompanyIds.some((id) => !Number.isSafeInteger(id) || id <= 0)
  ) {
    throw new Error('Invalid company ID in supplier export companies.');
  }

  if (uniqueCompanyIds.length <= SUPPLIER_EXPORT_COMPANY_CHUNK_SIZE) {
    const response = await fetchSuppliers(buildSupplierExportFilterRequest(baseParams), params.filterBy);
    return response.items;
  }

  // companyId must be in exactly one chunk; it normally already is (it heads the user's hierarchy).
  const uniqueIds = [...new Set([companyId, ...uniqueCompanyIds])];
  const controller = new AbortController();
  try {
    const tasks = toChunks(uniqueIds, SUPPLIER_EXPORT_COMPANY_CHUNK_SIZE).map(
      (exportCompanyIds) => () =>
        fetchSuppliers(
          buildSupplierExportFilterRequest({ ...baseParams, exportCompanyIds }),
          params.filterBy,
          controller.signal
        )
    );
    const responses = await runWithConcurrency(tasks, SUPPLIER_EXPORT_MAX_CONCURRENT);
    return responses.flatMap((response) => response.items).sort(bySupplierCompanyName);
  } catch (error) {
    controller.abort();
    throw error;
  }
};

// Fetches suppliers scoped to companyId with isExport: true, then re-applies the grid's
// current column filters/sort to the freshly-fetched data (mirrors what the user currently
// sees in the grid, over the export-specific response).
export const fetchSuppliersForExport = async (
  params: FetchSuppliersForExportParams
): Promise<ManageSupplier[]> => {
  const parsedCompanyId = Number(params.companyId);
  if (!Number.isSafeInteger(parsedCompanyId) || parsedCompanyId <= 0) {
    throw new Error('Missing or invalid companyId for supplier export.');
  }

  const items = (await fetchExportItems(params, parsedCompanyId)).map(mapSupplier);

  return process(items, {
    filter: params.filter,
    sort: params.sort,
    skip: 0,
    take: items.length,
  }).data;
};
